import React, { useState, useMemo } from 'react';
import Table from 'react-bootstrap/Table';
import MultiSelectDropdown from '../../../globalComponents/form/dropdowns/MultiSelectDropdown';
import './dataownermatrix.css';

/**
 * Returns cell style based on the progress percentage.
 * Uses soft, premium HSL/RGB colors matching modern dashboard aesthetics.
 */
const getCellClassName = (completed, total) => {
  if (total === 0) return 'cell-empty';
  if (completed === total) return 'cell-completed';
  if (completed > 0) return 'cell-in-progress';
  return 'cell-zero';
};

/**
 * A highly reusable Progress Matrix Table component with built-in:
 * - Data Owner Search/Filter
 * - Data Owner Multiselect Dropdown Filter
 * - Multi-Column Sorting (ASC/DESC) with visual indicators
 */
const ProgressMatrixTable = ({
  matrixData,
  loading,
  title = "Progress Matrix",
  subtitle = "Detailed completion per month/period (answered / assigned)",
  icon = "📊"
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOwnerIds, setSelectedOwnerIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Dynamically extract dropdown options from matrix rows
  const dataOwnerOptions = useMemo(() => {
    if (!matrixData || !matrixData.rows) return [];
    return matrixData.rows.map(row => ({
      value: row.userId,
      label: `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email || `User ${row.userId}`
    }));
  }, [matrixData]);

  // Handle header sorting click
  const requestSort = (key, index = null) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.index === index && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.index === index && sortConfig.direction === 'desc') {
      direction = null; // Reset sort
    }
    setSortConfig({ key, index, direction });
  };

  // Get current sort icon class
  const getSortIcon = (key, index = null) => {
    if (sortConfig.key !== key || sortConfig.index !== index || !sortConfig.direction) {
      return 'fas fa-sort sort-icon-inactive';
    }
    return sortConfig.direction === 'asc'
      ? 'fas fa-sort-up sort-icon-active'
      : 'fas fa-sort-down sort-icon-active';
  };

  // Process data (filter + sort)
  const processedRows = useMemo(() => {
    if (!matrixData || !matrixData.rows) return [];

    let result = [...matrixData.rows];

    // 1. Apply Search Filter
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(row => {
        const fullName = `${row.firstName || ''} ${row.lastName || ''}`.toLowerCase();
        const email = (row.email || '').toLowerCase();
        return fullName.includes(term) || email.includes(term);
      });
    }

    // 2. Apply Multiselect Dropdown Filter
    if (selectedOwnerIds.length > 0) {
      const selectedSet = new Set(selectedOwnerIds.map(Number));
      result = result.filter(row => selectedSet.has(Number(row.userId)));
    }

    // 3. Apply Sort Filter
    if (sortConfig.key && sortConfig.direction) {
      const { key, index, direction } = sortConfig;
      const multiplier = direction === 'asc' ? 1 : -1;

      result.sort((a, b) => {
        let valA, valB;

        if (key === 'owner') {
          valA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          valB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          return valA.localeCompare(valB) * multiplier;
        }

        if (key === 'period' && index !== null) {
          valA = a.cells[index]?.pct ?? -1;
          valB = b.cells[index]?.pct ?? -1;
        } else if (key === 'annual') {
          valA = a.annualCell?.pct ?? -1;
          valB = b.annualCell?.pct ?? -1;
        } else if (key === 'qualitative') {
          valA = a.fyTotalCell?.pct ?? -1;
          valB = b.fyTotalCell?.pct ?? -1;
        } else if (key === 'overall') {
          valA = a.totalPct ?? -1;
          valB = b.totalPct ?? -1;
        }

        if (valA < valB) return -1 * multiplier;
        if (valA > valB) return 1 * multiplier;
        return 0;
      });
    }

    return result;
  }, [matrixData, searchTerm, selectedOwnerIds, sortConfig]);

  if (loading) {
    return (
      <div className="matrix-card loading-state">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading matrix...</p>
      </div>
    );
  }

  if (!matrixData || !matrixData.rows || matrixData.rows.length === 0) {
    return (
      <div className="matrix-card empty-state">
        <span className="empty-icon">{icon}</span>
        <h3 className="empty-title">No Matrix Data</h3>
        <p className="empty-text">Select filters above to load the progress matrix.</p>
      </div>
    );
  }

  const { periods } = matrixData;

  return (
    <div className="matrix-card">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="chart-card-header" style={{
        padding: '18px 24px 16px',
        borderBottom: '1px solid rgba(99,102,241,0.08)',
        background: 'linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(139,92,246,0.04) 100%)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            {/* Accent bar */}
            <div className="chart-card-accent" style={{
              width: '4px',
              height: '36px',
              borderRadius: '3px',
              flexShrink: 0,
              marginRight: '14px',
              background: 'linear-gradient(180deg, #8B5CF6 0%, #8B5CF688 100%)'
            }} />

            {/* Icon + text */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              <div style={{
                width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(139,92,246,0.22) 0%, rgba(139,92,246,0.44) 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px',
                border: '1px solid rgba(139,92,246,0.33)',
              }}>
                {icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <h3 style={{
                  margin: 0, fontSize: '15px', fontWeight: '700',
                  color: '#1E293B', lineHeight: '1.2',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {title}
                </h3>
                {subtitle && (
                  <p style={{
                    margin: 0, fontSize: '12px', color: '#64748B', marginTop: '2px',
                    lineHeight: '1.4',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 24px 24px' }}>
        {/* Reusable Filters Area */}
        <div className="matrix-filters-area" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {/* Data Owner Multiselect Dropdown */}
          <div style={{ width: '220px' }}>
            <MultiSelectDropdown
              options={dataOwnerOptions}
              selected={selectedOwnerIds}
              onChange={setSelectedOwnerIds}
              placeholder="Select Data Owners"
              searchPlaceholder="Search data owners..."
            />
          </div>

          {/* Search Box */}
          <div className="matrix-search-input-wrapper" style={{ margin: 0 }}>
            <i className="fas fa-search matrix-search-icon" aria-hidden="true" />
            <input
              type="text"
              className="matrix-search-input"
              placeholder="Filter by Data Owner name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

      <div className="table-responsive matrix-table-container">
        <Table bordered hover className="matrix-table">
          <thead>
            <tr>
              <th
                className="header-owner sticky-col header-sortable"
                onClick={() => requestSort('owner')}
              >
                <div className="header-cell-content">
                  Data Owner
                  <span className="sort-icon-container">
                    <i className={getSortIcon('owner')} aria-hidden="true" />
                  </span>
                </div>
              </th>
              {periods.map((p, idx) => (
                <th
                  key={idx}
                  className="header-period header-sortable"
                  onClick={() => requestSort('period', idx)}
                >
                  <div className="header-cell-content">
                    {p}
                    <span className="sort-icon-container">
                      <i className={getSortIcon('period', idx)} aria-hidden="true" />
                    </span>
                  </div>
                </th>
              ))}
              <th
                className="header-annual header-sortable"
                onClick={() => requestSort('annual')}
              >
                <div className="header-cell-content">
                  Annual
                  <span className="sort-icon-container">
                    <i className={getSortIcon('annual')} aria-hidden="true" />
                  </span>
                </div>
              </th>
              <th
                className="header-qual header-sortable"
                onClick={() => requestSort('qualitative')}
              >
                <div className="header-cell-content">
                  FY Total (Qualitative)
                  <span className="sort-icon-container">
                    <i className={getSortIcon('qualitative')} aria-hidden="true" />
                  </span>
                </div>
              </th>
              <th
                className="header-total header-sortable"
                onClick={() => requestSort('overall')}
              >
                <div className="header-cell-content">
                  Overall Progress
                  <span className="sort-icon-container">
                    <i className={getSortIcon('overall')} aria-hidden="true" />
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {processedRows.length > 0 ? (
              processedRows.map((row, rIdx) => (
                <tr key={row.userId || rIdx}>
                  <td className="cell-owner sticky-col">
                    <div className="owner-info">
                      <span className="owner-name">{row.firstName} {row.lastName}</span>
                      <span className="owner-email">{row.email}</span>
                    </div>
                  </td>
                  {row.cells.map((cell, cIdx) => (
                    <td
                      key={cIdx}
                      className={`cell-data ${getCellClassName(cell.completed, cell.total)}`}
                      title={`${cell.completed} of ${cell.total} tasks completed for ${cell.label}`}
                    >
                      {cell.total > 0 ? (
                        <div className="cell-content">
                          <span className="cell-fraction">{cell.completed}/{cell.total}</span>
                          <span className="cell-pct">{Math.round(cell.pct)}%</span>
                        </div>
                      ) : (
                        <span className="cell-dash">—</span>
                      )}
                    </td>
                  ))}
                  
                  {/* Annual column */}
                  <td
                    className={`cell-data ${getCellClassName(row.annualCell.completed, row.annualCell.total)}`}
                    title={`${row.annualCell.completed} of ${row.annualCell.total} annual tasks completed`}
                  >
                    {row.annualCell.total > 0 ? (
                      <div className="cell-content">
                        <span className="cell-fraction">{row.annualCell.completed}/{row.annualCell.total}</span>
                        <span className="cell-pct">{Math.round(row.annualCell.pct)}%</span>
                      </div>
                    ) : (
                      <span className="cell-dash">—</span>
                    )}
                  </td>

                  {/* FY Total Qualitative column */}
                  <td
                    className={`cell-data ${getCellClassName(row.fyTotalCell.completed, row.fyTotalCell.total)}`}
                    title={`${row.fyTotalCell.completed} of ${row.fyTotalCell.total} qualitative tasks completed`}
                  >
                    {row.fyTotalCell.total > 0 ? (
                      <div className="cell-content">
                        <span className="cell-fraction">{row.fyTotalCell.completed}/{row.fyTotalCell.total}</span>
                        <span className="cell-pct">{Math.round(row.fyTotalCell.pct)}%</span>
                      </div>
                    ) : (
                      <span className="cell-dash">—</span>
                    )}
                  </td>

                  {/* Overall summary column */}
                  <td className="cell-overall">
                    <div className="overall-content">
                      <div className="overall-stats">
                        <span className="overall-fraction">{row.totalCompleted}/{row.totalAssigned}</span>
                        <span className="overall-pct">{Math.round(row.totalPct)}%</span>
                      </div>
                      <div className="progress-bar-container">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${row.totalPct}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={periods.length + 4} className="text-center py-4 text-muted">
                  No data matches the selected filter
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
    </div>
  );
};

export default ProgressMatrixTable;
